import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot, orderBy, updateDoc, doc, getDoc } from 'firebase/firestore';
import Toast from '../components/Toast';

const NotificationsContext = createContext();

export function useNotifications() {
  return useContext(NotificationsContext);
}

export function NotificationsProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const { currentUser } = useAuth();
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [notificationTime, setNotificationTime] = useState('09:00');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const sendHugNotification = useCallback(async (notification) => {
    try {
      if (!('Notification' in window)) return;
      
      if (Notification.permission === 'granted') {
        const { fromName } = notification;
        new Notification('New Hug! 🤗', {
          body: `${fromName} sent you a hug!`,
          icon: '/icon-192x192.png',
          badge: '/icon-192x192.png',
          timestamp: new Date().getTime()
        });
      }
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('to', '==', currentUser.uid),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const notification = {
            id: change.doc.id,
            ...change.doc.data(),
            timestamp: change.doc.data().timestamp?.toDate()
          };

          // Only show notification for new items
          if (change.doc.data().timestamp?.toDate() > new Date(Date.now() - 5000)) {
            // Handle different notification types
            switch (notification.type) {
              case 'hug':
                sendHugNotification(notification);
                break;
              case 'emotion':
                showToast(
                  `${notification.fromName || 'Someone'} sent you ${notification.emoji}`,
                  'emotion'
                );
                break;
              default:
                break;
            }
          }

          // Update notifications state
          setNotifications(prev => {
            const exists = prev.some(n => n.id === notification.id);
            if (!exists) {
              return [notification, ...prev];
            }
            return prev;
          });
        }
      });
    });

    return () => unsubscribe();
  }, [currentUser, sendHugNotification]);

  const markAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const sendTestNotification = () => {
    if (!('Notification' in window)) return;
    
    try {
      new Notification('Notifications Enabled! 🎉', {
        body: "You'll receive daily reminders to help you stay on track with your goals.",
        icon: '/logo192.png' // Make sure this icon exists in your public folder
      });
    } catch (error) {
      console.error('Error sending test notification:', error);
    }
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      console.error('This browser does not support notifications');
      return 'unsupported';
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      
      // Send test notification if permission was granted
      if (permission === 'granted') {
        sendTestNotification();
      }
      
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setPermissionStatus('denied');
      return 'denied';
    }
  };

  useEffect(() => {
    // Check initial notification permission status
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    }
  }, []);

  const updateNotificationTime = (newTime) => {
    setNotificationTime(newTime);
    // Optionally save to user preferences in Firebase
    if (currentUser) {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        updateDoc(userRef, {
          notificationTime: newTime
        });
      } catch (error) {
        console.error('Error updating notification time:', error);
      }
    }
  };

  // Add effect to load notification time from user preferences
  useEffect(() => {
    if (!currentUser) return;

    const userRef = doc(db, 'users', currentUser.uid);
    getDoc(userRef).then(docSnap => {
      if (docSnap.exists() && docSnap.data().notificationTime) {
        setNotificationTime(docSnap.data().notificationTime);
      }
    }).catch(error => {
      console.error('Error loading notification time:', error);
    });
  }, [currentUser]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 5000); // Hide after 3 seconds
  };

  // Add function to handle sending hugs
  const handleHugSent = (toUser) => {
    showToast(`Sent a hug to ${toUser.displayName || 'friend'} 🤗`, 'hug');
  };

  const value = {
    notifications,
    markAsRead,
    requestNotificationPermission,
    permissionStatus,
    notificationTime,
    updateNotificationTime,
    sendHugNotification,
    showToast,
    handleHugSent,
    toast,
    setToast
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <Toast 
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
        showCloseButton={false}
      />
    </NotificationsContext.Provider>
  );
} 